import {useMemo} from 'react';

import {useSelector} from 'react-redux';
import {PlainRoute} from 'react-router';

import {CurrentUserSelector} from 'app/current-user/api';
import {
  DOCUMENTS_HOME,
  KB_DASHBOARD_URL,
  POLICIES_DASHBOARD_URL,
  PROCEDURES_HOME,
  PROCESS_DASHBOARD_URL,
  QUIZZES_HOME,
  TAGS_DASHBOARD_URL,
  TASKS_HOME,
  TEAMS_HOME,
  USERS_HOME,
} from 'app/urls';
import {gt} from 'app/utils/i18n';

// number os links shown in the navbar
export const NUM_MAIN_LINKS = 4;

interface NavLinkType {
  slug: string;
  title: string;
  icon: string;
  href: string;
  iconType?: string;
}

export type AnnotatedNavLinkType = NavLinkType & {
  isMain: boolean;
};

export const getNavLinksMap = (): Record<string, NavLinkType> => ({
  procedures: {
    slug: 'procedures',
    title: gt('Procedures'),
    icon: 'home',
    href: PROCEDURES_HOME,
  },
  policies: {
    slug: 'policies',
    title: gt('Policies'),
    icon: 'umbrella',
    href: POLICIES_DASHBOARD_URL,
  },
  processes: {
    slug: 'processes',
    title: gt('Processes'),
    icon: 'copy',
    href: PROCESS_DASHBOARD_URL,
  },
  tasks: {
    slug: 'tasks',
    title: gt('Tasks'),
    icon: 'tasks',
    href: TASKS_HOME,
  },
  quizzes: {
    slug: 'quizzes',
    title: gt('Quizzes'),
    icon: 'check',
    href: QUIZZES_HOME,
  },
  teams: {
    slug: 'teams',
    title: gt('Teams'),
    icon: 'users',
    href: TEAMS_HOME,
  },
  members: {
    slug: 'members',
    title: gt('Members'),
    icon: 'user',
    href: USERS_HOME,
  },
  tags: {
    slug: 'tags',
    title: gt('Manage Tags'),
    icon: 'tags',
    href: TAGS_DASHBOARD_URL,
  },
  kbase: {
    slug: 'kbase',
    title: gt('Knowledge Bases'),
    icon: 'book-open',
    iconType: 'far',
    href: KB_DASHBOARD_URL,
  },
  documents: {
    slug: 'documents',
    title: gt('Documents'),
    icon: 'folder-open',
    iconType: 'far',
    href: DOCUMENTS_HOME,
  },
});

export const isNavLinkActive = (
  linkSlug: string,
  routes: Array<PlainRoute>
): boolean =>
  routes.filter((r) => r.props?.activateNavLink === linkSlug).length > 0;

export function useNavLinks(
  filter: 'main' | 'secondary' | void
): Array<AnnotatedNavLinkType> {
  const linksMap = useMemo(getNavLinksMap, []);

  const navSlugs: string = useSelector(
    (state) => CurrentUserSelector(state).data!.navigation_links
  );
  const links = useMemo<Array<NavLinkType>>(() => {
    const slugs = navSlugs.split(',');
    return slugs.map((slug) => linksMap[slug]);
  }, [linksMap, navSlugs]);
  const annotatedLinks = useMemo(() => addMainField(links), [links]);

  return useMemo(() => {
    switch (filter) {
      case 'main':
        return annotatedLinks.filter((l) => l.isMain);

      case 'secondary':
        return annotatedLinks.filter((l) => !l.isMain);

      default:
        return annotatedLinks;
    }
  }, [annotatedLinks, filter]);
}

export function addMainField(
  links: Array<NavLinkType>
): Array<AnnotatedNavLinkType> {
  return links.map<AnnotatedNavLinkType>((link, index) => ({
    ...link,
    isMain: index < NUM_MAIN_LINKS,
  }));
}
